import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { theme } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { TreemapChart } from 'echarts/charts';
import { TooltipComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { useTranslation } from 'react-i18next';
import { DEFAULT_CHART_PALETTE } from './reportingCategoryColors';
import {
    buildTreemapPath,
    buildTreemapTree,
    findTreemapNodeById,
    getTreemapFocusData,
} from './reportingChartDataPrep';
import { hierarchyColumnKeys, isHierarchicalDataset } from './reportingDatasetShape';

echarts.use([TooltipComponent, TreemapChart, CanvasRenderer]);

const TREEMAP_STYLE = {
    gapWidth: 0,
    borderWidth: 0,
};

const BREADCRUMB_MAX_LABEL = 28;

function truncateLabel(label, max = BREADCRUMB_MAX_LABEL) {
    const text = String(label ?? '');
    return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

function TreemapBreadcrumb({ path, onNavigate, t, token }) {
    if (!path.length) return null;

    const segments = [
        { id: null, label: t('All') },
        ...path.map(node => ({ id: node.id, label: node.name })),
    ];

    return (
        <nav
            aria-label={t('Treemap drill-down')}
            style={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                columnGap: 6,
                rowGap: 2,
                padding: '5px 10px',
                marginBottom: 6,
                fontSize: 11,
                lineHeight: '18px',
                color: token.colorTextSecondary,
                background: token.colorFillAlter,
                borderRadius: token.borderRadiusSM,
                border: `1px solid ${token.colorBorderSecondary}`,
            }}
        >
            {segments.map((seg, index) => {
                const isLast = index === segments.length - 1;
                const label = truncateLabel(seg.label, isLast ? 40 : BREADCRUMB_MAX_LABEL);

                return (
                    <React.Fragment key={seg.id ?? 'root'}>
                        {index > 0 && (
                            <RightOutlined
                                aria-hidden
                                style={{ fontSize: 9, color: token.colorTextQuaternary }}
                            />
                        )}
                        {isLast ? (
                            <span
                                title={seg.label}
                                style={{ color: token.colorText, fontWeight: 500 }}
                            >
                                {label}
                            </span>
                        ) : (
                            <button
                                type="button"
                                title={seg.label}
                                onClick={() => onNavigate(seg.id)}
                                style={{
                                    border: 'none',
                                    background: 'none',
                                    padding: 0,
                                    margin: 0,
                                    cursor: 'pointer',
                                    font: 'inherit',
                                    fontSize: 'inherit',
                                    lineHeight: 'inherit',
                                    color: token.colorTextSecondary,
                                    textDecoration: 'none',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.color = token.colorPrimary;
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.color = token.colorTextSecondary;
                                }}
                            >
                                {label}
                            </button>
                        )}
                    </React.Fragment>
                );
            })}
        </nav>
    );
}

/**
 * Drill-down treemap: filters data to the focused subtree (ECharts zoomToNode only pans).
 */
export default function HierarchicalTreemap({
    dataset,
    vizSpec = {},
    chartRef,
    remountKey = '',
    style,
    expanded = false,
}) {
    const { t } = useTranslation();
    const { token } = theme.useToken();
    const [focusId, setFocusId] = useState(null);

    const columns = dataset?.columns ?? [];
    const rows = dataset?.rows ?? [];
    const encoding = vizSpec?.encoding ?? {};
    const palette = Array.isArray(vizSpec?.colors) && vizSpec.colors.length > 0
        ? vizSpec.colors
        : DEFAULT_CHART_PALETTE;

    const keys = useMemo(
        () => hierarchyColumnKeys(columns, encoding),
        [columns, encoding],
    );

    const fullTree = useMemo(
        () => (isHierarchicalDataset(columns) ? buildTreemapTree(rows, keys) : []),
        [columns, rows, keys],
    );

    useEffect(() => {
        setFocusId(null);
    }, [remountKey, rows.length]);

    const displayData = useMemo(
        () => getTreemapFocusData(fullTree, focusId),
        [fullTree, focusId],
    );

    const path = useMemo(
        () => buildTreemapPath(fullTree, focusId),
        [fullTree, focusId],
    );

    const option = useMemo(() => ({
        color: palette,
        tooltip: {
            trigger: 'item',
            formatter: (p) => `<strong>${p.name}</strong><br/>${Number(p.value) || 0}`,
        },
        series: [{
            type: 'treemap',
            roam: false,
            nodeClick: false,
            breadcrumb: { show: false },
            left: 0,
            right: 0,
            top: 4,
            bottom: 4,
            label: {
                show: true,
                fontSize: expanded ? 12 : 10,
                overflow: 'truncate',
                ellipsis: '…',
            },
            upperLabel: { show: false },
            itemStyle: TREEMAP_STYLE,
            levels: [
                { itemStyle: TREEMAP_STYLE },
                { itemStyle: TREEMAP_STYLE },
                { itemStyle: TREEMAP_STYLE },
            ],
            emphasis: {
                itemStyle: {
                    borderColor: token.colorPrimary,
                    borderWidth: 1,
                },
            },
            data: displayData,
        }],
    }), [displayData, expanded, palette, token.colorPrimary]);

    const onChartClick = useCallback((params) => {
        const id = params?.data?.id ?? params?.name;
        if (!id) return;
        const node = findTreemapNodeById(fullTree, id);
        if (node?.children?.length) setFocusId(node.id);
    }, [fullTree]);

    const onEvents = useMemo(() => ({ click: onChartClick }), [onChartClick]);

    const onBreadcrumbClick = useCallback((id) => {
        setFocusId(id);
    }, []);

    if (!fullTree.length) return null;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            <TreemapBreadcrumb
                path={path}
                onNavigate={onBreadcrumbClick}
                t={t}
                token={token}
            />
            <ReactECharts
                key={`${remountKey}-treemap`}
                ref={chartRef}
                echarts={echarts}
                option={option}
                style={{ flex: 1, minHeight: 160, ...style }}
                onEvents={onEvents}
                notMerge
                lazyUpdate
            />
        </div>
    );
}
